/**
 * @description
 * Platform OAuth service for managing platform connection and authentication.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date January 8, 2025
 */

import { BaseAPIClient } from "../core/base-client";
import type { 
  PlatformOAuthConfig, 
  PlatformOAuthInitResult, 
  PlatformConnectionResult 
} from "./types";

/**
 * Platform OAuth service for managing platform connection
 */
export class PlatformOAuthService extends BaseAPIClient {
  /**
   * Get platform OAuth configuration status
   */
  async getPlatformOAuthConfig(): Promise<PlatformOAuthConfig> {
    return this.request("/platform/oauth/config");
  }

  /**
   * Initialize platform OAuth with token
   */
  async initializePlatformOAuth(data: {
    authToken: string;
    refreshToken?: string;
    tokenExpiresAt?: string;
    userInfo?: {
      email: string;
      name: string;
      role: string;
    };
  }): Promise<PlatformOAuthInitResult> {
    return this.request("/platform/oauth/initialize", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  /**
   * Verify platform connection
   */
  async verifyPlatformConnection(): Promise<PlatformConnectionResult> {
    return this.request("/platform/oauth/verify", {
      method: "POST",
    });
  }

  /**
   * Refresh platform token
   */
  async refreshPlatformToken(): Promise<{
    success: boolean;
    message: string;
    error?: string;
  }> {
    return this.request("/platform/oauth/refresh", {
      method: "POST",
    });
  }

  /**
   * Disconnect platform OAuth
   */
  async disconnectPlatform(): Promise<{
    success: boolean;
    message: string;
  }> {
    return this.request("/platform/oauth/config", {
      method: "DELETE",
    });
  }
}

// Export singleton instance
export const platformOAuthService = new PlatformOAuthService();
